import request, { IWorkspaceResponse } from '/@/api/http/request'
import { CameraType, CameraMode } from '/@/types/live-stream'
import { GimbalResetMode } from '/@/types/drone-control'

const API_PREFIX = '/control/api/v1'

export interface PostPayloadAuthBody {
  payload_index: string
}

// 获取负载控制权
export async function postPayloadAuth (sn: string, body: PostPayloadAuthBody): Promise<IWorkspaceResponse<null>> {
  const resp = await request.post(`${API_PREFIX}/devices/${sn}/authority/payload`, body)
  return resp.data
}

// 画面拖动控制
export enum PayloadCommandsEnum {
  CameraCrameZoom = 'camera_frame_zoom', // 负载控制-框选变焦,框选变焦功能为在相机镜头的视野范围内，框选镜头中的目标区域，将会自动把画面切过去自动变焦+转云台
  CameraModeSwitch = 'camera_mode_switch', // 负载控制—切换相机模式{"0":"拍照","1":"录像","2":"智能低光","3":"全景拍照"}
  CameraPhotoTake = 'camera_photo_take', // 负载控制—开始拍照
  CameraPhotoStop = 'camera_photo_stop', // 负载控制—停止拍照(停止拍照指令，目前仅支持全景拍照模式)
  CameraRecordingStart = 'camera_recording_start', // 负载控制—开始录像
  CameraRecordingStop = 'camera_recording_stop', // 负载控制—停止录像
  CameraScreenDrag = 'camera_screen_drag', // 负载控制—画面拖动控制
  CameraFocalLengthSet = 'camera_focal_length_set', // 负载控制—变焦
  GimbalReset = 'gimbal_reset', // 负载控制—重置云台
  CameraAim = 'camera_aim', // 负载控制—双击成为 AIM
  CameraScreenSplit = 'camera_screen_split', // 负载控制—分屏
  CameraPointFocusAction = 'camera_point_focus_action', // 负载控制—点对焦
  CameraLookAt = 'camera_look_at', // 负载控制—看向目标点lookat 功能,指飞行器将从当前朝向转向实际经纬高度指定的点，在 M30/M30T 机型上建议使用锁定机头的方式，仅云台转动场景下在抵达云台限位角后 lookat 功能将出现异常
  IrMeteringModeSet = 'ir_metering_mode_set', // 负载控制—红外测距模式设置{"0":"关闭测温","1":"点测温","2":"区域测温"}
  IrMeteringAreaSet = 'ir_metering_area_set', // 负载控制—红外测距区域设置
  IrMeteringPointSet = 'ir_metering_point_set', // 负载控制—红外测距点设置
}

export interface PostCameraCrameZoomBody {
  payload_index: string,
  camera_mode: CameraMode, // 相机类型{"ir":"红外","wide":"广角","zoom":"变焦"}
  locked: boolean, // {"false":"仅云台转，机身不转","true":"锁定机头，云台和机身一起转"}
  x: number, // 目标框左上角点坐标x
  y: number, // 目标框左上角点坐标y
  width: number, // 目标框宽度
  height: number, // 目标框高度
}

export interface PostCameraModeBody {
  payload_index: string,
  camera_mode: CameraMode
}

export interface PostCameraPhotoBody {
  payload_index: string
}

export interface DeletePostCameraPhotoBody {
  payload_index: string
}

export interface PostCameraRecordingBody {
  payload_index: string
}

export interface DeleteCameraRecordingParams {
  payload_index: string
}

export interface PostCameraFocalLengthBody {
  payload_index: string,
  camera_type: CameraType,
  zoom_factor: number
}

export interface PostGimbalResetBody{
  payload_index: string,
  reset_mode: GimbalResetMode,
}

export interface PostCameraAimBody{
  payload_index: string,
  camera_type: CameraType,
  locked: boolean, // {"false":"仅云台转，机身不转","true":"锁定机头，云台和机身一起转"}
  x: number,
  y: number,
}

export interface PostCameraScreenDragBody{
  payload_index: string,
  locked: boolean, // {"false":"仅云台转，机身不转","true":"锁定机头，云台和机身一起转"}
  pitch_speed: number, // 云台 pitch 速度
  yaw_speed: number, // 云台 yaw 速度(仅不锁机头时才生效)
}

export interface PostCameraScreenSplitBody{
  payload_index: string,
  enable: boolean, // 是否使能分屏
}

export interface PostCameraPointFocusActionBody{
  payload_index: string,
  camera_type: CameraType,
  x: number,
  y: number,
}

// 看向目标点,lookat 功能指飞行器将从当前朝向转向实际经纬高度指定的点，在 M30/M30T 机型上建议使用锁定机头的方式，仅云台转动场景下在抵达云台限位角后 lookat 功能将出现异常
export interface PostCameraLookAtBody{
  payload_index: string,
  locked: boolean, // {"false":"仅云台转，机身不转","true":"锁定机头，云台和机身一起转"}
  latitude: number, // 目标点纬度
  longitude: number, // 目标点经度
  height: number, // 目标点高度
}

export interface PostIrMeteringModeSetBody{
  payload_index: string,
  mode: number, // 红外测距模式{"0":"关闭测温","1":"点测温","2":"区域测温"}
}

export interface PostIrMeteringPointSetBody{
  payload_index: string,
  x: number,
  y: number,
}

export interface PostIrMeteringAreaSetBody{
  payload_index: string,
  x: number,
  y: number,
  width: number,
  height: number,
}

export type PostPayloadCommandsBody = {
  cmd: PayloadCommandsEnum.CameraCrameZoom,
  data: PostCameraCrameZoomBody
} | {
  cmd: PayloadCommandsEnum.CameraModeSwitch,
  data: PostCameraModeBody
} | {
  cmd: PayloadCommandsEnum.CameraPhotoTake,
  data: PostCameraPhotoBody
} | {
  cmd: PayloadCommandsEnum.CameraPhotoStop,
  data: DeletePostCameraPhotoBody
} | {
  cmd: PayloadCommandsEnum.CameraRecordingStart,
  data: PostCameraRecordingBody
} | {
  cmd: PayloadCommandsEnum.CameraRecordingStop,
  data: DeleteCameraRecordingParams
} | {
  cmd: PayloadCommandsEnum.CameraFocalLengthSet,
  data: PostCameraFocalLengthBody
} | {
  cmd: PayloadCommandsEnum.GimbalReset,
  data: PostGimbalResetBody
} | {
  cmd: PayloadCommandsEnum.CameraAim,
  data: PostCameraAimBody
} | {
  cmd: PayloadCommandsEnum.CameraScreenDrag,
  data: PostCameraScreenDragBody
} | {
  cmd: PayloadCommandsEnum.CameraScreenSplit,
  data: PostCameraScreenSplitBody
} | {
  cmd: PayloadCommandsEnum.CameraPointFocusAction,
  data: PostCameraPointFocusActionBody
} | {
  cmd: PayloadCommandsEnum.CameraLookAt,
  data: PostCameraLookAtBody
}| {
  cmd: PayloadCommandsEnum.IrMeteringModeSet,
  data: PostIrMeteringModeSetBody
} | {
  cmd: PayloadCommandsEnum.IrMeteringAreaSet,
  data: PostIrMeteringAreaSetBody
}| {
  cmd: PayloadCommandsEnum.IrMeteringPointSet,
  data: PostIrMeteringPointSetBody
}

// 发送负载名称
export async function postPayloadCommands (sn: string, body: PostPayloadCommandsBody): Promise<IWorkspaceResponse<null>> {
  const resp = await request.post(`${API_PREFIX}/devices/${sn}/payload/commands`, body)
  return resp.data
}

// 发送负载名称
export async function postPayloadRcCommands (sn: string, body: PostPayloadCommandsBody): Promise<IWorkspaceResponse<null>> {
  const resp = await request.post(`${API_PREFIX}/rc/rc-payload/commands`, {
    ...body,
    sn: sn
  })
  return resp.data
}
